'use client'

import React, { useMemo, useState } from 'react'
import { safeDecodeCollectionSlug } from '@/utils/collection-object'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'
import { API_URL, COLLECTIONS_VERSION } from '@/utils/constants'
import { fetcher } from '@/utils/fetcher'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import BitcoinIcon from '@/icons/bitcoin-filled'
import PercentChange from '@/components/percent-change'
import useEscrows from '@/utils/hooks/useEscrows'
import { isMonkeAskSale } from '@/utils/monke-market'
import useSoldEscrows from '@/utils/hooks/useSoldEscrows'
import { useCollectionVWAPRange } from '@/utils/hooks/useCollectionVWAP'
import { useConfirmedCollectionSales } from '@/lib/sales-feed/hooks'
import { useCollectionVolume } from '@/utils/hooks/useSalesVolume'
import useCollectionMeta from '@/utils/hooks/useCollectionMeta'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useApiStatus from '@/utils/hooks/useApiStatus'
import useNatOwners from '@/utils/hooks/useNatOwners'
import isRune from '@/utils/is-rune'
import isFungible from '@/utils/is-fungible'
import { getTapTickerFromSlug } from '@/utils/tap-token'
import { formatRune } from '@/utils/format-rune'
import { formatSatsPer } from '@/utils/format-sats'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import { getSatoshiPriceUSD } from '@/utils/hooks/useSatoshiPriceUSD'
import { useMark } from '@/lib/quotes/hooks'
import Ticker from '@/components/ticker'
import { formatBtc } from '@/components/tv-chart/price-format'
import { formatUsdPer, formatUsdPerBillion } from '@/utils/format-usd'
import formatNumber from '@/utils/format-number'
import { LineChart, List, ExternalLink, Map as MapIcon } from 'lucide-react'
import Link from 'next/link'
import classNames from 'classnames'
import { CollectionQuickView, QuickViewBody, QuickViewMetric } from './collection-quickview'
import { isCollectionObjectSlug } from '@/utils/collection-object'

// NAT ships with no description in the collection API, so surface the tagline
// in the About modal. Other fungibles use their own `collection.description`.
const NAT_DESCRIPTION_OVERRIDE = `NAT is the only censorship-resistant, no-fork-needed, working solution to Bitcoin's fading block reward and it's live today. Where to buy NAT? Only on Ordinals Wallet.`

const HEADER_ACTION_CLASSNAME =
	'inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-muted px-2.5 text-[11px] font-medium text-muted-foreground transition-colors duration-200 hover:border-foreground/30 hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'

const formatBtc2 = (amountSats: number) => {
	if (!amountSats) return '0.00'
	const btc = amountSats / 1e8
	return btc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const abbreviateNumber = (num: number) => {
	if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B'
	if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M'
	if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K'
	return num.toFixed(2)
}

interface CollectionHeaderProps {
	collection: any
	data: any
	slug: string
	hideStats?: boolean
	/** Current chart range pushed up from whichever chart instance is mounted
	 * (inline chart tab or the Insights modal). Drives the header's Change
	 * metric so its label + value match what the user sees on the chart. */
	chartRange?: { label: string; value: number } | null
	/** Callback the modal chart uses to report range changes back up. */
	onChartRangeChange?: (label: string, value: number) => void
	/** 'strip' (default) — horizontal metrics strip used as the page header.
	 *  'insights' — full vertical insights body (icon+name, price, embedded
	 *  chart, market stats grid, about, socials) used inline on mobile chart
	 *  tabs for non-NAT collections. */
	mode?: 'strip' | 'insights'
	/** Insights mode only: triggers the "View activity" button in the metrics
	 * heading row. Hooked to the parent's tab switch on mobile. */
	onViewActivity?: () => void
	/** Insights mode only: max pixel width for the embedded chart. */
	chartMaxWidth?: number
	/** Insights mode only: actions rendered beside the icon/name heading (e.g.
	 * the mobile chart/activity quick-jump icons). Falls back to the bitmap map
	 * action when not provided. */
	headerLeading?: React.ReactNode
	headerActions?: React.ReactNode
}

/**
 * Unified collection detail header — used for both fungible tokens (runes,
 * brc20, tap, drc20, bel20, alkane, NAT) and NFT collections. Renders the
 * quick-view icon+name trigger on the left and a horizontal-scrolling strip
 * of market metrics on the right. Per-metric `show` flags hide columns whose
 * source data isn't available for the given protocol (e.g. Market Cap is
 * meaningless for NFTs; Owners aren't populated for most fungibles).
 */
export function CollectionHeader({
	collection,
	data,
	slug,
	hideStats,
	chartRange,
	onChartRangeChange,
	mode = 'strip',
	onViewActivity,
	chartMaxWidth,
	headerActions,
	headerLeading
}: CollectionHeaderProps) {
	const isTapDmtNat = slug?.toUpperCase() === 'TAP-DMT-NAT'
	const isTapToken = !!slug?.toLowerCase().startsWith('tap-')
	const tapTicker = getTapTickerFromSlug(slug)
	const isRuneCollection = isRune(slug)
	const isFungibleCollection = isFungible(slug) || Boolean(tapTicker)
	const isBitmapCollection = slug === 'bitmap'
	const isMonkeCollection = slug === 'nodemonkes'
	const { data: apiStatus } = useApiStatus()
	const chainTip: number | undefined = isBitmapCollection
		? apiStatus?.chain_height ?? apiStatus?.indexer_height
		: undefined
	const isCollectionObject = isCollectionObjectSlug(slug)

	const { data: escrows } = useEscrows(slug)
	const { data: soldEscrows } = useSoldEscrows(slug)
	const { data: collectionMeta } = useCollectionMeta(slug)
	const { data: tokenPrices } = useTokenPrices()
	const { exchangeRate } = useExchangeRate()
	// The live fair value: the chart's mark, pushed as it moves.
	// Keyed on the page's slug: the `collection` prop is absent on some
	// header variants, and a null key never subscribes.
	const mark = useMark(slug ?? collection?.slug ?? null)
	const { data: natOwnersPage } = useNatOwners(Boolean(tapTicker), 1, tapTicker || 'dmt-nat')
	const { data: runeData } = useSWR(
		isRuneCollection ? `${API_URL}/rune/${slug.replace('rune-', '')}/stats` : null,
		fetcher
	)

	// Cross-marketplace ("global") signals, available for collection detail
	// pages via the per-collection sales endpoints. These complement the
	// OW-only floor (live escrow listings) and OW volume (collection /stats).
	// SWR keys are shared with the Activity feed / chart, so these are cache
	// hits rather than extra network round-trips.
	// Recent sales from the live feed the activity panel already streams.
	const globalSales = useConfirmedCollectionSales(slug)
	const { data: collectionVolume } = useCollectionVolume(slug)

	// Global volume = lifetime cross-marketplace volume across CLASSIFIED markets
	// only. We exclude marketplace "0" (Unknown: OTC / wallet transfers with a
	// price / misclassified moves) which otherwise inflates totals far above real
	// marketplace activity and creates 24h outliers. Counts OW, Satflow, Magic
	// Eden, OrdSwap, Gamma — matching what aggregators like Satflow report.
	const globalVolumeSats = useMemo(() => {
		const buckets = collectionVolume?.buckets
		if (!Array.isArray(buckets)) return 0
		return buckets.reduce((acc: number, b: any) => {
			const byMarket = b?.by_marketplace || {}
			let classified = 0
			for (const mk of Object.keys(byMarket)) {
				if (mk !== '0') classified += byMarket[mk]?.volume_sats || 0
			}
			return acc + classified
		}, 0)
	}, [collectionVolume])

	// Recent executions are historical prices, never a live global ask.
	const [recentSaleSince] = useState(() => Math.floor(Date.now() / 1000) - 86400)
	const recentSaleLowSats = useMemo(() => {
		if (isFungibleCollection || !Array.isArray(globalSales) || globalSales.length === 0) return 0
		const singleItemPrices = globalSales
			.filter(
				(s) =>
					s &&
					s.price_sats > 0 &&
					(slug !== 'nodemonkes' || isMonkeAskSale(s)) &&
					typeof s.block_timestamp === 'number' &&
					s.block_timestamp >= recentSaleSince &&
					(!s.inscriptions_in_tx || s.inscriptions_in_tx <= 1)
			)
			.map((s) => s.price_sats)
		return singleItemPrices.length > 0 ? Math.min(...singleItemPrices) : 0
	}, [recentSaleSince, globalSales, isFungibleCollection, slug])

	// Hourly VWAP for the last 7d — aligned with the chart's default 1W range
	// so the header's 7D Change renders the same percent the chart shows.
	const [weekRange] = useState(() => {
		const now = Math.floor(Date.now() / 1000)
		return { start: now - 7 * 86400, end: now }
	})
	const { data: hourlyVwap } = useCollectionVWAPRange(slug, '1h', weekRange.start, weekRange.end)

	const { data: homepageCollections } = useSWRImmutable(
		!isCollectionObject
			? `${API_URL}/${COLLECTIONS_VERSION}?order=VolumeDayDesc&limit=1&search=${encodeURIComponent(
					slug
			  )}`
			: null,
		fetcher
	)

	// Per-token best ask from live listings (min satoshi_price / amount). Only
	// fungibles price per-token; for NFTs
	// `amount` isn't a token quantity (e.g. on bitmap it carries the bitmap
	// number) so dividing by it produced bogus floors like 6.69 sats from a
	// 12400-sat listing.
	const bestAskPerToken = useMemo(() => {
		if (!isFungibleCollection) return null
		if (escrows && Array.isArray(escrows) && escrows.length > 0) {
			const pricesPerToken = escrows
				.filter((e: any) => e.satoshi_price && e.amount)
				.map((e: any) => e.satoshi_price / parseFloat(e.amount || '1'))
			if (pricesPerToken.length > 0) return Math.min(...pricesPerToken)
		}
		return null
	}, [escrows, isFungibleCollection])

	const lastSalePerToken = useMemo(() => {
		if (!isFungibleCollection) return null
		if (soldEscrows && Array.isArray(soldEscrows) && soldEscrows.length > 0) {
			const sortedSold = [...soldEscrows]
				.filter((e: any) => e.satoshi_price && e.amount)
				.sort(
					(a: any, b: any) =>
						new Date(b.bought_at || b.sold_at || 0).getTime() -
						new Date(a.bought_at || a.sold_at || 0).getTime()
				)
			if (sortedSold.length > 0) {
				const lastSale = sortedSold[0]
				return lastSale.satoshi_price / parseFloat(lastSale.amount || '1')
			}
		}
		return null
	}, [isFungibleCollection, soldEscrows])

	const tokenPrice = useMemo(() => {
		if (bestAskPerToken !== null && isFinite(bestAskPerToken)) return bestAskPerToken
		if (lastSalePerToken !== null && isFinite(lastSalePerToken)) return lastSalePerToken
		if (isFungibleCollection) {
			return parseFloat(
				(tokenPrices || []).find((e: any) => e.slug === safeDecodeCollectionSlug(slug))
					?.floor_price_per || collection?.floor_price_per
			)
		}
		// NFT floor: lowest active listing, else most recent sale, else the
		// backend's published floor.
		if (escrows && Array.isArray(escrows) && escrows.length > 0) {
			const prices = escrows
				.filter((e: any) => typeof e.satoshi_price === 'number' && e.satoshi_price > 0)
				.map((e: any) => e.satoshi_price as number)
			if (prices.length > 0) return Math.min(...prices)
		}
		if (soldEscrows && Array.isArray(soldEscrows) && soldEscrows.length > 0) {
			const sortedSold = [...soldEscrows]
				.filter((e: any) => typeof e.satoshi_price === 'number' && e.satoshi_price > 0)
				.sort((a: any, b: any) => (b.sold_at || 0) - (a.sold_at || 0))
			if (sortedSold.length > 0) return sortedSold[0].satoshi_price
		}
		return Number(data?.floor_price ?? collection?.floor_price) || 0
	}, [
		slug,
		tokenPrices,
		collection,
		data,
		escrows,
		soldEscrows,
		bestAskPerToken,
		lastSalePerToken,
		isFungibleCollection
	])

	const { sats, per } = useMemo(() => formatSatsPer(tokenPrice), [tokenPrice])
	const listed = typeof escrows?.length === 'number' ? escrows.length : data?.listed || 0
	const sales = Math.max(soldEscrows?.length || 0, data?.sales || 0)

	// Circulating/total supply in whole tokens — drives both Market Cap and
	// listed%. Each protocol exposes supply differently: TAP tokens via
	// `tap_circulating_supply`, runes via the rune stats endpoint (respecting
	// divisibility), everything else falls back to the collection meta's
	// `total_supply`.
	const circulatingTokens = useMemo<number | undefined>(() => {
		if (typeof data?.tap_circulating_supply === 'number') return data.tap_circulating_supply
		if (typeof collection?.tap_circulating_supply === 'number')
			return collection.tap_circulating_supply
		if (runeData?.supply) {
			const divisibility = Number(runeData?.divisibility ?? 0)
			const raw = Number(runeData.supply)
			const scaled = raw / Math.pow(10, divisibility)
			if (isFinite(scaled)) return scaled
		}
		const totalSupply = collectionMeta?.total_supply ?? collection?.total_supply
		return typeof totalSupply === 'number' ? totalSupply : undefined
	}, [data, collection, runeData, collectionMeta])

	const marketCap =
		typeof circulatingTokens === 'number' && tokenPrice ? circulatingTokens * tokenPrice : undefined

	// True only when the SSR/server response actually attached
	// `tap_circulating_supply` from Tapalytics. Used to gate Market Cap so we
	// don't fall through to floor × total-supply for TAP tokens that
	// Tapalytics doesn't cover yet.
	const hasTapCirculating =
		typeof data?.tap_circulating_supply === 'number' ||
		typeof collection?.tap_circulating_supply === 'number'

	const homepageCollection = Array.isArray(homepageCollections?.collections)
		? homepageCollections.collections.find((c: any) => c?.slug === slug)
		: undefined
	const volume_total =
		(homepageCollection?.volume_all_time ?? data?.volume_total ?? data?.volume_all_time) || 0
	const volume_24h = (homepageCollection?.volume_day ?? data?.volume_day) || 0

	// Default 7D change — overridden by `chartRange` once the chart reports
	// back. Mirrors chart.tsx `visibleInfo` exactly so the header chip matches
	// the chart's percent change chip bucket-for-bucket.
	const weekChange = useMemo(() => {
		if (!Array.isArray(hourlyVwap) || hourlyVwap.length <= 1) return 0
		const hourly = hourlyVwap
			.filter((d: any) => d && typeof d.time === 'number' && typeof d.vwap === 'number')
			.sort((a: any, b: any) => a.time - b.time)
		if (hourly.length === 0) return 0
		const lastIdx = hourly.length - 1
		const lastTime = hourly[lastIdx].time
		const fromTime = lastTime - 7 * 24 * 60 * 60
		const foundIdx = hourly.findIndex((d: any) => d.time >= fromTime)
		const startIdx = foundIdx < 0 ? 0 : foundIdx
		const startVal = hourly[startIdx].vwap
		const endVal = hourly[lastIdx].vwap
		if (startVal > 0) return ((endVal - startVal) / startVal) * 100
		return 0
	}, [hourlyVwap])

	// One 7D number everywhere: the fair band's level today against seven
	// days ago -- live from the quote hub, else the API's field, the same
	// one the homepage prints. The chart's visible-range change no longer
	// replaces it; the escrow VWAP only stands in while the API has no tape.
	// `collectionMeta` is the live record; `data` can be a server-rendered
	// copy from before the field existed.
	const fairWeekChange = [
		mark?.change_week,
		collectionMeta?.change_week_fair,
		data?.change_week_fair,
		homepageCollection?.change_week_fair
	].find((v: unknown): v is number => typeof v === 'number' && Number.isFinite(v))
	const displayChangeLabel = '7D'
	const displayChangeValue = fairWeekChange ?? weekChange

	const formatUSD = (satAmount: number) => {
		const usd = (satAmount / 1e8) * exchangeRate
		const formattedUsd = formatUsdPer(usd).usd
		return formattedUsd > 0 ? '$' + abbreviateNumber(formattedUsd) : null
	}

	const listedPercent =
		typeof circulatingTokens === 'number' && circulatingTokens > 0
			? (listed / circulatingTokens) * 100
			: null

	const ownersAmount: React.ReactNode =
		tapTicker && typeof natOwnersPage?.totalHolders === 'number'
			? natOwnersPage.totalHolders.toLocaleString()
			: isRuneCollection && typeof runeData?.owners === 'number'
			? runeData.owners.toLocaleString()
			: typeof data?.owners === 'number'
			? data.owners.toLocaleString()
			: '—'

	const supplyDisplay: React.ReactNode =
		isRuneCollection && runeData?.supply
			? formatRune(runeData.supply, runeData)
			: typeof circulatingTokens === 'number'
			? formatNumber(circulatingTokens)
			: null

	const inscriptionNumberRange = useMemo(() => {
		const lowest = Number(
			collectionMeta?.lowest_inscription_num ??
				collection?.lowest_inscription_num ??
				data?.lowest_inscription_num
		)
		const highest = Number(
			collectionMeta?.highest_inscription_num ??
				collection?.highest_inscription_num ??
				data?.highest_inscription_num
		)
		if (!Number.isFinite(lowest) || !Number.isFinite(highest) || lowest <= 0 || highest <= 0) {
			return null
		}
		return `#${Math.min(lowest, highest)}-#${Math.max(lowest, highest)}`
	}, [
		collectionMeta?.highest_inscription_num,
		collectionMeta?.lowest_inscription_num,
		collection?.highest_inscription_num,
		collection?.lowest_inscription_num,
		data?.highest_inscription_num,
		data?.lowest_inscription_num
	])

	// Each metric is shown only when its underlying source has a real value.
	// Protocols vary in what they expose (NAT has holders, most others don't;
	// some collections have no VWAP yet, etc.) — hiding empty columns avoids a
	// row of zeros that reads as "data" but is really just missing fields.
	const hasChangeData =
		fairWeekChange !== undefined || (Array.isArray(hourlyVwap) && hourlyVwap.length > 1)
	const ownersCount =
		tapTicker && typeof natOwnersPage?.totalHolders === 'number'
			? natOwnersPage.totalHolders
			: isRuneCollection && typeof runeData?.owners === 'number'
			? runeData.owners
			: typeof data?.owners === 'number'
			? data.owners
			: null
	const hasOwners = typeof ownersCount === 'number' && ownersCount > 0
	const hasSupply =
		(isRuneCollection && Boolean(runeData?.supply)) ||
		(typeof circulatingTokens === 'number' && circulatingTokens > 0)

	const metricCandidates: Array<QuickViewMetric & { show: boolean }> = [
		{
			// Fair value, live. The number the chart's candles close at, quoted
			// per item and moving with bitcoin between trades.
			label: 'Fair Value',
			primary: mark ? (
				<Ticker value={mark.fair_sats} format={(v) => formatBtc(v)} snapBelow={0.00001} />
			) : null,
			secondary: mark ? (
				<Ticker
					value={(mark.fair_sats / 1e8) * (exchangeRate || 0)}
					format={(v) =>
						`$${getSatoshiPriceUSD((v / (exchangeRate || 1)) * 1e8, exchangeRate || 1)}`
					}
					tint={false}
					snapBelow={0.00001}
				/>
			) : null,
			isBtc: true,
			show: Boolean(mark)
		},
		{
			label: 'Market Cap',
			primary: typeof marketCap === 'number' ? formatBtc2(marketCap) : null,
			secondary: typeof marketCap === 'number' ? formatUSD(marketCap) : null,
			isBtc: true,
			// Tapalytics publishes `circulating_supply` for any TAP token, so
			// floor × circulating is a defensible market cap for the whole `tap-`
			// family. For non-TAP fungibles (runes/brc20) the equivalent would
			// be floor × total-supply, which is inflated by non-circulating
			// tokens; hide there until we wire up a per-token price feed.
			show:
				isTapToken &&
				hasTapCirculating &&
				typeof marketCap === 'number' &&
				isFinite(marketCap) &&
				marketCap > 0
		},
		{
			label: isMonkeCollection ? '24H Ask-sale Low' : '24H Sale Low',
			primary: recentSaleLowSats > 0 ? formatSatoshis(recentSaleLowSats) : null,
			secondary:
				recentSaleLowSats > 0 ? `$${getSatoshiPriceUSD(recentSaleLowSats, exchangeRate)}` : null,
			isBtc: recentSaleLowSats > 0,
			// Cross-marketplace floor only makes sense for NFTs; fungibles price
			// per-token via the ask / latest-sale hierarchy instead.
			show: !isFungibleCollection && recentSaleLowSats > 0
		},
		{
			label:
				isFungibleCollection && tokenPrice
					? bestAskPerToken !== null
						? 'Best Ask'
						: lastSalePerToken !== null
						? 'Last Sale'
						: 'Reference Price'
					: 'OW Floor',
			primary: tokenPrice ? (
				isFungibleCollection ? (
					<span>
						{sats}
						<span className="ml-1 text-[12px] font-medium text-foreground/55">{per}</span>
					</span>
				) : (
					formatSatoshis(tokenPrice)
				)
			) : null,
			// NAT's per-token USD rounds to "$0.00", so render it as price-per-
			// billion ("$228.95/B") matching the feed. Other collections keep
			// the standard per-unit USD line.
			secondary: tokenPrice
				? isTapDmtNat
					? formatUsdPerBillion(tokenPrice * 1e-8 * exchangeRate)
					: `$${getSatoshiPriceUSD(tokenPrice, exchangeRate)}`
				: null,
			// Show the BTC icon next to the value for NFT floors (which are
			// denominated in BTC); fungible per-token pricing sits in `sats` so no
			// icon is needed there.
			isBtc: !isFungibleCollection && !!tokenPrice,
			show: !!tokenPrice && tokenPrice > 0
		},
		{
			label: `${displayChangeLabel} Change`,
			primary: <PercentChange value={displayChangeValue} />,
			isBtc: false,
			show: hasChangeData
		},
		{
			label: '24h Vol',
			primary: formatBtc2(volume_24h),
			secondary: volume_24h ? formatUSD(volume_24h) : null,
			isBtc: true,
			show: volume_24h > 0
		},
		{
			label: 'Global Vol',
			primary: formatBtc2(globalVolumeSats),
			secondary: globalVolumeSats ? formatUSD(globalVolumeSats) : null,
			isBtc: true,
			show: globalVolumeSats > 0
		},
		{
			label: 'OW Vol',
			primary: formatBtc2(volume_total),
			secondary: volume_total ? formatUSD(volume_total) : null,
			isBtc: true,
			show: volume_total > 0
		},
		{
			label: 'Sales',
			primary: sales.toLocaleString(),
			secondary: listed > 0 ? `${listed.toLocaleString()} listed` : null,
			isBtc: false,
			show: sales > 0
		},
		{
			label: 'Supply',
			primary: supplyDisplay,
			secondary:
				!isTapDmtNat && typeof listedPercent === 'number'
					? `${listedPercent.toFixed(2)}% listed`
					: null,
			isBtc: false,
			show: hasSupply
		},
		{
			label: 'Inscription Range',
			primary: inscriptionNumberRange,
			isBtc: false,
			show: !isFungibleCollection && Boolean(inscriptionNumberRange)
		},
		{
			label: 'Owners',
			primary: ownersAmount,
			isBtc: false,
			show: hasOwners
		}
	]

	const metrics: QuickViewMetric[] = metricCandidates
		.filter((m) => m.show)
		.map(({ show: _show, ...rest }) => rest)

	const displayDescription = isTapDmtNat ? NAT_DESCRIPTION_OVERRIDE : collection?.description

	const bitmapMapAction = (
		<>
			<Link
				href={`/collection/${encodeURIComponent(slug)}/game`}
				className={HEADER_ACTION_CLASSNAME}
				aria-label={`View map for ${collection?.name || 'bitmap'}`}
			>
				<MapIcon className="size-3" strokeWidth={2.5} />
				<span>View map</span>
			</Link>
			{typeof chainTip === 'number' && (
				<span
					className={classNames(HEADER_ACTION_CLASSNAME, 'font-mono')}
					title="Latest Bitcoin block: every new block is a new bitmap"
					aria-label={`Chain tip block ${chainTip.toLocaleString('en-US')}`}
				>
					Tip <span className="text-[#6941C6] dark:text-[#B692F6]">#{chainTip.toLocaleString('en-US')}</span>
				</span>
			)}
		</>
	)
	const inlineHeaderActions = headerActions ? (
		<div className="flex w-full shrink-0 items-center gap-2">{headerActions}</div>
	) : null

	// NAT's per-token USD rounds to "$0.00", so render it as price-per-
	// billion ("$228.95/B") matching the rest of the NAT UI.
	const priceUsd = tokenPrice
		? isTapDmtNat
			? formatUsdPerBillion(tokenPrice * 1e-8 * exchangeRate)
			: `$${getSatoshiPriceUSD(tokenPrice, exchangeRate)}`
		: null
	const priceDisplay = tokenPrice ? (
		<span>
			{isFungibleCollection ? sats : formatSatoshis(tokenPrice)}
			<span className="ml-2 text-[18px] font-medium text-foreground/50">
				{isFungibleCollection ? per : 'BTC'}
			</span>
		</span>
	) : (
		<span className="inline-flex items-center gap-2">
			{formatSatoshis(0)}
			<BitcoinIcon className="size-6" />
		</span>
	)

	// The quick view draws the fair-price line, so its headline is the fair
	// value -- the number the line ends at -- not one venue's listing floor.
	// It leads with the dollar figure, the line's own unit, and carries the
	// BTC figure under it. Tokens keep their per-unit reference price.
	const fairHeadline =
		mark && !isFungibleCollection ? (
			exchangeRate ? (
				<Ticker
					value={(mark.fair_sats / 1e8) * exchangeRate}
					format={(v) => `$${getSatoshiPriceUSD((v / exchangeRate) * 1e8, exchangeRate)}`}
					snapBelow={0.01}
				/>
			) : (
				<span>
					<Ticker value={mark.fair_sats} format={(v) => formatBtc(v)} snapBelow={0.00001} />
					<span className="ml-2 text-[18px] font-medium text-foreground/50">BTC</span>
				</span>
			)
		) : null
	const fairHeadlineUsd =
		mark && !isFungibleCollection && exchangeRate ? (
			<Ticker
				value={mark.fair_sats}
				format={(v) => `${formatBtc(v)} BTC`}
				tint={false}
				snapBelow={0.00001}
			/>
		) : null

	if (mode === 'insights') {
		const twitterHandle: string | undefined = collection?.socials?.twitter || collection?.twitter
		const twitterSearchUrl = twitterHandle
			? `https://x.com/${String(twitterHandle).replace(/^@/, '')}`
			: collection?.name
			? `https://x.com/search?q=${encodeURIComponent(collection.name)}`
			: null

		return (
			<>
				<QuickViewBody
                    headerLeading={headerLeading}
					collection={collection}
					slug={slug}
					headerActions={isBitmapCollection ? bitmapMapAction : headerActions}
					metrics={metrics}
					description={
						isMonkeCollection ? (
							<>
								{displayDescription} Ask-sale averages use single-item signed listing fills. Offer
								fills, bundles, self-transfers and fee-only matches are excluded. Empty periods are
								not filled with old prices.
							</>
						) : (
							displayDescription
						)
					}
					priceDisplay={priceDisplay}
					priceUsd={priceUsd}
					changeValue={hasChangeData ? displayChangeValue : undefined}
					changeSource={isMonkeCollection ? 'Ask-sale avg' : undefined}
					changeLabel={displayChangeLabel}
					chartMaxWidth={chartMaxWidth}
					onRangeChange={onChartRangeChange}
					metricsHeaderRight={
						onViewActivity ? (
							<button
								type="button"
								onClick={onViewActivity}
								className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
							>
								<List size={12} />
								View activity
							</button>
						) : undefined
					}
				/>
				{twitterSearchUrl && (
					<a
						href={twitterSearchUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="mx-5 mb-4 mt-2 flex min-w-0 items-center gap-4 rounded-xl bg-foreground/[0.10] p-4 transition-colors hover:bg-foreground/[0.14]"
					>
						<svg viewBox="0 0 24 24" className="size-6 shrink-0 fill-foreground">
							<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
						</svg>
						<div className="min-w-0 flex-1 border-l border-border pl-4">
							<div className="truncate text-base font-semibold text-foreground">
								What&apos;s Happening with {collection?.name || 'this collection'}?
							</div>
							<div className="mt-0.5 flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
								See what people are saying on X
								<ExternalLink size={10} />
							</div>
						</div>
					</a>
				)}
			</>
		)
	}

	return (
		<div className="w-full max-w-[100vw] border-y border-border bg-card text-foreground">
			<div className="flex flex-col gap-3 px-5 py-3 md:px-6 md:py-3.5 xl:flex-row xl:items-center xl:gap-0">
				<div className="min-w-0 xl:w-[280px] xl:flex-none xl:pr-5">
					{isBitmapCollection ? (
						<div className="flex items-center gap-3">
							<div className="shrink-0 rounded-2xl border border-border bg-muted p-1">
								<CollectionIcon
									className="size-12 overflow-hidden rounded-xl"
									collection={collection}
								/>
							</div>
							<div className="min-w-0 flex-1">
								<h2 className="text-xl font-semibold tracking-tight text-foreground">
									{collection?.name}
									<VerifiedBadge slug={slug} />
								</h2>
								<div className="mt-2 flex flex-wrap items-center gap-2">{bitmapMapAction}</div>
							</div>
						</div>
					) : (
						<div className="flex min-w-0 flex-col items-stretch gap-2">
							<CollectionQuickView
								collection={collection}
								slug={slug}
								headerActions={headerActions}
								metrics={metrics}
								description={
									isMonkeCollection ? (
										<>
											{displayDescription} Ask-sale averages use single-item signed listing fills.
											Offer fills, bundles, self-transfers and fee-only matches are excluded. Empty
											periods are not filled with old prices.
										</>
									) : (
										displayDescription
									)
								}
								priceDisplay={fairHeadline ?? priceDisplay}
								priceUsd={fairHeadlineUsd ?? priceUsd}
								changeValue={hasChangeData ? displayChangeValue : undefined}
								changeSource={isMonkeCollection ? 'Ask-sale avg' : undefined}
								changeLabel={displayChangeLabel}
								onRangeChange={onChartRangeChange}
							>
								<div
									role="button"
									tabIndex={0}
									aria-label={`Open chart for ${collection?.name}`}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault()
											;(e.currentTarget as HTMLDivElement).click()
										}
									}}
									className="group flex min-w-0 flex-1 cursor-pointer items-center gap-3.5 rounded-md transition-colors duration-200 hover:bg-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
								>
									<div className="shrink-0 rounded-[16px] border border-border bg-muted p-1">
										<CollectionIcon
											className="size-[52px] overflow-hidden rounded-[12px] md:size-[56px]"
											collection={collection}
										/>
									</div>

									<div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
										<div className="flex flex-wrap items-center gap-2 leading-none">
											<h2
												title={collection?.name}
												className="max-w-full truncate text-[20px] font-semibold leading-none tracking-[-0.03em] text-foreground md:text-[1.5rem]"
											>
												{collection?.name}
											</h2>
											<VerifiedBadge slug={slug} />
											<span className="inline-flex h-6 items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 text-[11px] font-medium text-muted-foreground transition-colors duration-200 group-hover:border-foreground/30 group-hover:bg-accent group-hover:text-foreground">
												<LineChart className="size-3" strokeWidth={2.5} />
												<span>Chart</span>
											</span>
										</div>
									</div>
								</div>
							</CollectionQuickView>
							{inlineHeaderActions}
						</div>
					)}
				</div>

				{!hideStats && metrics.length > 0 && (
					<div className="overflow-x-auto scrollbar-hide xl:flex xl:min-w-0 xl:flex-1 xl:self-stretch xl:border-l xl:border-border xl:pl-5">
						<div className="flex min-w-max items-stretch divide-x divide-border xl:h-full xl:min-h-[64px]">
							{metrics.map((metric, index) => (
								<div
									key={metric.label}
									className={`flex min-w-[118px] flex-col justify-center gap-1.5 px-4 xl:min-w-[108px] xl:px-3 2xl:min-w-[124px] 2xl:px-5 ${
										index === 0 ? 'pl-0' : ''
									} ${index === metrics.length - 1 ? 'pr-0' : ''}`}
								>
									<div className="text-[10px] font-semibold text-muted-foreground">
										{metric.label}
									</div>
									<div className="flex flex-col gap-1">
										<div className="flex min-w-0 items-center gap-1 text-[17px] font-semibold leading-none tracking-[-0.02em] text-foreground">
											<span className="truncate">{metric.primary}</span>
											{metric.isBtc && <BitcoinIcon className="size-3.5 shrink-0" />}
										</div>
										<div className="min-h-[12px] text-[11px] leading-none text-muted-foreground/80">
											{metric.secondary || '\u00A0'}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
